import { useCallback, useEffect, useState } from 'react';
import { getGameDefinition } from '../games/registry';
import { isNonogramPuzzle } from '../games/nonogram/types';
import { getActiveRawMs, getSessionElapsedMs } from '../run/runReducer';
import { getSkipState } from '../run/skipPolicy';
import type { RunController } from '../run/useRunController';
import type { AppSettings } from '../run/types';
import { formatDelta, formatDuration } from '../shared/time';
import { Countdown } from './Countdown';
import { ResultsScreen } from './ResultsScreen';
import { RulesModal } from './RulesModal';
import { RunActionsModal } from './RunActionsModal';

type Props = {
  controller: RunController;
  settings: AppSettings;
  onHome: () => void;
  onReplay: (seed: string) => void;
  onAbandon: () => void;
  onRestart: (seed: string) => void;
};

export function RunScreen({ controller, settings, onHome, onReplay, onAbandon, onRestart }: Props) {
  const { run } = controller;
  const [now, setNow] = useState(() => performance.now());
  const [rulesOpen, setRulesOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [runActionsOpen, setRunActionsOpen] = useState(false);
  const [showSlowLoading, setShowSlowLoading] = useState(false);

  useEffect(() => {
    if (!run || !['splash', 'playing', 'outcome', 'loading'].includes(run.phase)) return;
    const handle = window.setInterval(() => setNow(performance.now()), 100);
    return () => window.clearInterval(handle);
  }, [run?.phase, run]);

  useEffect(() => {
    if (!run || run.phase !== 'outcome') return;
    const duration = settings.reducedMotion ? 150 : 1_500;
    const handle = window.setTimeout(controller.outcomeComplete, duration);
    return () => window.clearTimeout(handle);
  }, [controller.outcomeComplete, run, settings.reducedMotion]);

  useEffect(() => {
    if (!run || run.phase !== 'loading') return;
    const duration = settings.reducedMotion ? 60 : 350;
    const handle = window.setTimeout(controller.loadingComplete, duration);
    return () => window.clearTimeout(handle);
  }, [controller.loadingComplete, run, settings.reducedMotion]);

  useEffect(() => {
    setShowSlowLoading(false);
    if (!run || run.phase !== 'loading' || settings.reducedMotion) return;
    const handle = window.setTimeout(() => setShowSlowLoading(true), 250);
    return () => window.clearTimeout(handle);
  }, [run, settings.reducedMotion]);

  const closeRules = useCallback(() => setRulesOpen(false), []);

  if (!run) return null;
  if (run.phase === 'results') {
    return <ResultsScreen run={run} onHome={onHome} onReplay={onReplay} />;
  }
  if (run.phase === 'error') {
    const canRetry = run.puzzles.length === 0;
    return (
      <main className="center-screen" data-run-phase="error">
        <section className="panel error-panel" role="alert">
          <p className="eyebrow">Run recovery</p>
          <h1>This run could not continue.</h1>
          <p>{run.error}</p>
          <div className="button-row">
            {canRetry && (
              <button className="button" onClick={controller.retryPreparation}>
                Retry preparation
              </button>
            )}
            <button className="button button--secondary" onClick={onAbandon}>
              Abandon and return home
            </button>
          </div>
        </section>
      </main>
    );
  }
  if (run.phase === 'preparing') {
    const percent = run.generationProgress.total
      ? Math.round((run.generationProgress.completed / run.generationProgress.total) * 100)
      : 0;
    return (
      <main className="center-screen" data-run-phase="preparing">
        <section className="preparing" aria-live="polite">
          <span className="wordmark">GAUNTLE</span>
          <p className="eyebrow">Preparing deterministic run</p>
          <h1>{percent}%</h1>
          <div className="progress-track" aria-label={`${percent}% prepared`}>
            <span style={{ width: `${percent}%` }} />
          </div>
          <p>
            {run.generationProgress.gameName
              ? `Validating ${run.generationProgress.gameName}…`
              : 'Starting local puzzle worker…'}
          </p>
          <small>Seed: {run.seed}</small>
          <button
            className="button button--secondary"
            onClick={() => void controller.cancelPreparation().then(onHome)}
          >
            Cancel preparation
          </button>
        </section>
      </main>
    );
  }

  const puzzle = run.puzzles[run.currentIndex];
  const definition = getGameDefinition(puzzle.gameId);
  const sessionElapsed = getSessionElapsedMs(run, now);
  const rawElapsed = getActiveRawMs(run, now);
  const skip = getSkipState(run.mode, definition.skip, rawElapsed);
  const skipAvailable = run.phase === 'playing' && skip.available;
  const currentResult = run.results[run.currentIndex];
  const Game = definition.Component;
  const sizeBadge =
    puzzle.gameId === 'nonogram' && isNonogramPuzzle(puzzle.payload)
      ? `${puzzle.payload.width}w × ${puzzle.payload.height}h`
      : null;
  const outcomeSummary = currentResult
    ? currentResult.outcome === 'skipped'
      ? `${formatDelta(currentResult.deltaMs)} penalty · skipped`
      : currentResult.outcome === 'failed'
        ? `${formatDelta(currentResult.deltaMs)} penalty`
        : currentResult.deltaMs === 0
          ? 'Flawless'
          : currentResult.deltaMs < 0
            ? `${formatDelta(currentResult.deltaMs)} bonus`
            : `${formatDelta(currentResult.deltaMs)} penalty`
    : '';

  return (
    <main className="run-shell" data-run-phase={run.phase}>
      <header className="run-header">
        <div className="game-identity">
          <div className="game-identity__name">
            <strong>{definition.displayName}</strong>
            {sizeBadge && <small className="game-size-badge">{sizeBadge}</small>}
          </div>
          <span>
            game {run.currentIndex + 1} of {run.puzzles.length}
          </span>
        </div>
        <span className="wordmark">GAUNTLE</span>
        <div className="run-tools">
          <time aria-label={`Session elapsed ${formatDuration(sessionElapsed)}`}>
            {formatDuration(sessionElapsed)}
          </time>
          <button
            className="icon-button"
            aria-label="Open game rules"
            onClick={() => setRulesOpen(true)}
          >
            ?
          </button>
          <button
            className="icon-button"
            aria-label="Open run controls"
            onClick={() => setRunActionsOpen(true)}
          >
            ⋯
          </button>
        </div>
      </header>

      <div className="run-content">
        {run.phase === 'splash' && (
          <Countdown
            key={run.phaseRevision}
            gameNumber={run.currentIndex + 1}
            gameName={definition.displayName}
            reducedMotion={settings.reducedMotion}
            onFinished={controller.splashComplete}
          />
        )}
        {run.phase === 'playing' && (
          <Game
            key={`${run.id}:${run.currentIndex}`}
            puzzle={puzzle}
            savedState={run.gameStates[String(run.currentIndex)]}
            disabled={rulesOpen || runActionsOpen}
            reducedMotion={settings.reducedMotion}
            onStateChange={controller.saveGameState}
            onCharge={(key, amountMs, label) => {
              controller.charge(key, amountMs, label);
              setToast(`${label}: ${formatDelta(amountMs)}`);
              window.setTimeout(() => setToast(null), 2_500);
            }}
            onComplete={controller.complete}
          />
        )}
        {run.phase === 'outcome' && currentResult && (
          <section
            className={`outcome-card outcome-card--${currentResult.outcome}`}
            aria-live="assertive"
          >
            <h1>{definition.displayName}</h1>
            <time>{formatDuration(currentResult.rawTimeMs)}</time>
            <p className="outcome-quip">{currentResult.message}</p>
            <p className="outcome-summary">{outcomeSummary}</p>
          </section>
        )}
        {run.phase === 'loading' && (
          <section className="loading-state" aria-live="polite">
            {showSlowLoading && (
              <h2>
                {run.currentIndex === run.puzzles.length - 1
                  ? 'Calculating results…'
                  : 'Slow connection. Loading next game…'}
              </h2>
            )}
          </section>
        )}
      </div>

      {run.phase === 'playing' && skip.visible && (
        <button
          className="skip-button"
          disabled={!skipAvailable}
          onClick={() => controller.skip(definition.skip.penaltyMs)}
          aria-label={
            skipAvailable
              ? `Skip ${definition.displayName}, costs ${definition.skip.penaltyMs / 1_000} seconds`
              : `Skip unlocks in ${Math.ceil(skip.remainingMs / 1_000)} seconds`
          }
        >
          {skipAvailable
            ? `Skip (+${definition.skip.penaltyMs / 1_000}s)`
            : `Skip in ${Math.ceil(skip.remainingMs / 1_000)}s`}
        </button>
      )}

      {rulesOpen && (
        <RulesModal
          gameName={definition.displayName}
          rules={definition.shortRules}
          onClose={closeRules}
        />
      )}
      {runActionsOpen && (
        <RunActionsModal
          seed={run.seed}
          onClose={() => setRunActionsOpen(false)}
          onRestart={() => {
            setRunActionsOpen(false);
            onRestart(run.seed);
          }}
          onAbandon={() => {
            setRunActionsOpen(false);
            onAbandon();
          }}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <span>{toast}</span>
          <button onClick={() => setToast(null)}>Dismiss</button>
        </div>
      )}
    </main>
  );
}
